import { useEffect, useState } from "react";
import { Waves, Sparkles } from "lucide-react";
import { api } from "@/lib/apiClient";

const RANGE_OPTIONS = [
    { key: 7, label: "This week" },
    { key: 30, label: "This month" },
    { key: 90, label: "Past 90 days" },
];

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const days = Math.floor(diffMs / 86400000);
    if (days < 1) return "today";
    if (days === 1) return "yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
}

export default function Patterns() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState({ themes: [], total_messages: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/patterns?days=${days}`)
            .then((r) => setData(r.data))
            .catch(() => setData({ themes: [], total_messages: 0 }))
            .finally(() => setLoading(false));
    }, [days]);

    return (
        <div className="max-w-4xl mx-auto w-full px-6 md:px-10 py-10 md:py-14" data-testid="patterns-page">
            <div className="mb-10 pl-12 md:pl-0">
                <div className="flex items-center gap-2 text-brand-charcoal/60 text-xs uppercase tracking-widest">
                    <Waves className="w-3 h-3 text-brand-spark" strokeWidth={2} />
                    Patterns
                </div>
                <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-brand-darkPurple leading-tight tracking-tight">
                    What keeps circling back.
                </h1>
                <p className="mt-3 text-brand-charcoal/75 max-w-2xl leading-relaxed">
                    Themes we notice, quietly, from what you've shared. Not diagnoses. Not to-do lists. Just a mirror.
                </p>
            </div>

            <div className="flex gap-2 mb-8" data-testid="patterns-range-toggle">
                {RANGE_OPTIONS.map((r) => (
                    <button
                        key={r.key}
                        data-testid={`range-${r.key}`}
                        onClick={() => setDays(r.key)}
                        className={`px-4 py-1.5 rounded-full text-sm transition-colors border ${
                            days === r.key
                                ? "bg-brand-darkPurple text-white border-brand-darkPurple"
                                : "bg-white text-brand-darkPurple border-brand-darkPurple/15 hover:bg-brand-cream"
                        }`}
                    >
                        {r.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-brand-darkPurple/60 font-serif italic">listening…</div>
            ) : data.themes.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-brand-darkPurple/15 bg-white/50 p-10 text-center" data-testid="patterns-empty">
                    <Sparkles className="w-6 h-6 text-brand-spark mx-auto mb-3" strokeWidth={1.5} />
                    <div className="font-serif italic text-xl text-brand-darkPurple">Not enough said yet.</div>
                    <p className="text-sm text-brand-charcoal/70 mt-2 max-w-md mx-auto">
                        As you have more conversations, themes will begin to surface here — gently, without judgment.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {data.themes.map((t, idx) => (
                        <article
                            key={t.theme}
                            data-testid={`pattern-${t.theme}`}
                            className="rounded-2xl bg-white border border-brand-darkPurple/10 shadow-soft-card p-6 md:p-7"
                            style={{ animationDelay: `${idx * 60}ms` }}
                        >
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div>
                                    <h3 className="font-serif text-2xl text-brand-darkPurple">{t.label}</h3>
                                    <p className="text-sm text-brand-charcoal/70 mt-1">
                                        You've come back to this <span className="font-medium text-brand-darkPurple">{t.count} {t.count === 1 ? "time" : "times"}</span> · last {timeAgo(t.last_seen)}.
                                    </p>
                                </div>
                                <div className="text-2xl font-serif text-brand-spark tabular-nums">×{t.count}</div>
                            </div>
                            {t.sample_messages?.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-brand-darkPurple/8 space-y-2">
                                    {t.sample_messages.slice(0, 2).map((s, i) => (
                                        <div key={i} className="text-sm text-brand-charcoal/70 italic pl-3 border-l-2 border-brand-spark/40 leading-relaxed">
                                            &ldquo;{s}{s.length >= 180 ? "…" : ""}&rdquo;
                                        </div>
                                    ))}
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            )}

            {!loading && data.total_messages > 0 && (
                <div className="mt-8 text-xs text-brand-charcoal/50">
                    Read from {data.total_messages} of your messages in the last {days} days.
                </div>
            )}
        </div>
    );
}
